import { useEffect, useState, useRef, useCallback } from "react";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import { isZoomApp } from "@utils/helpers";

const useSpeechToText = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);

  const {
    transcript,
    resetTranscript,
    browserSupportsSpeechRecognition,
    isMicrophoneAvailable,
    listening,
  } = useSpeechRecognition();

  // Check browser support and platform restrictions
  const isSupported = browserSupportsSpeechRecognition && !isZoomApp;

  // Set initial error for Zoom App users
  useEffect(() => {
    if (isZoomApp) {
      setError(
        "Speech recognition is not available in Zoom Apps due to security restrictions. Please type your message manually."
      );
    }
  }, []);

  // Setup error handling for speech recognition
  useEffect(() => {
    if (!browserSupportsSpeechRecognition) return;

    const recognition = SpeechRecognition.getRecognition();
    if (recognition) {
      // Handle errors
      recognition.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        if (event.error === "no-speech") {
          // Continue listening even if no speech detected
          return;
        }
        if (event.error === "not-allowed") {
          setError(
            "Microphone permission denied. Please allow microphone access."
          );
        } else if (event.error === "service-not-allowed") {
          setError(
            "Speech recognition is not available in Zoom Apps due to security restrictions. Please type your message manually."
          );
        } else if (event.error === "network") {
          setError(
            "Network error. Speech recognition requires an internet connection."
          );
        } else {
          setError(`Speech recognition error: ${event.error}`);
        }
        stopRecording();
      };
    }
  }, [browserSupportsSpeechRecognition]);

  // Timer for recording duration
  useEffect(() => {
    if (isRecording) {
      const startTime = Date.now();
      timerRef.current = setInterval(() => {
        setRecordingDuration(Math.floor((Date.now() - startTime) / 1000));
      }, 100);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setRecordingDuration(0);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isRecording]);

  const startRecording = useCallback(async () => {
    if (isZoomApp) {
      setError(
        "Speech recognition is not available in Zoom Apps due to security restrictions. Please type your message manually."
      );
      return;
    }

    if (!browserSupportsSpeechRecognition) {
      setError(
        "Speech recognition is not supported in this browser. Please use Chrome, Edge, or Safari."
      );
      return;
    }

    setError(null);
    setIsRecording(true);

    try {
      await SpeechRecognition.startListening({
        continuous: true,
        language: "en-US",
        interimResults: true,
      });
    } catch (err) {
      console.error("Failed to start speech recognition:", err);
      setError(
        "Failed to start speech recognition. Please check your microphone."
      );
      setIsRecording(false);
    }
  }, [browserSupportsSpeechRecognition]);

  const stopRecording = useCallback(() => {
    setIsRecording(false);
    SpeechRecognition.stopListening();
  }, []);

  const cancelRecording = useCallback(() => {
    stopRecording();
    resetTranscript();
  }, [stopRecording, resetTranscript]);

  // Format time as MM:SS
  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }, []);

  // Platform-specific info
  const platformInfo = {
    isZoomApp: isZoomApp,
    platform: isZoomApp ? "Zoom App" : "Browser",
  };

  return {
    // State
    isRecording,
    transcript,
    recordingDuration,
    error,
    isSupported,
    isMicrophoneAvailable,
    listening,

    // Actions
    startRecording,
    stopRecording,
    cancelRecording,
    resetTranscript,

    // Utilities
    formatTime,
    platformInfo,
  };
};

export default useSpeechToText;
